import Mui from 'materials';
import React, { useEffect, useMemo, useState } from 'react';
import { MinusCircle, PlusCircle } from 'lucide-react';
import { FormattedNumber, useIntl } from 'react-intl';
import _ from 'underscore';
import { randomUUID } from 'utils/random';

import {
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridPreProcessEditCellProps,
  GridRowEditStopReasons,
  GridRowModel,
  GridRowModesModel,
  GridRowsProp,
  useGridApiRef,
} from '@mui/x-data-grid';

import FlexBox from 'components/flex-box';
import DataGrid from 'components/data-grid';
import { isValidInteger } from 'utils/validate-numbers';
import { Line } from 'views/profiles-strategies/features/profiles/types/update-profile-input.type';
import { Box } from '@mui/material';

type Row = {
  id: string;
  period: number;
  average: number;
  rowIndex: number;
};

type ProfilesGridProps = {
  isLoading?: boolean;
  isDisabled?: boolean;
  rows: GridRowsProp<Row>;
  handleEditing: () => void;
  profile?: Line[] | undefined;
  errorDictionary: { [key: string]: boolean };
  setRows: React.Dispatch<React.SetStateAction<readonly Row[]>>;
  changeErrorDictionary: (errorDict: { [key: string]: boolean }) => void;
};

const ProfilesGrid: React.FC<ProfilesGridProps> = ({
  rows,
  profile,
  setRows,
  isDisabled,
  handleEditing,
  errorDictionary,
  changeErrorDictionary,
}) => {
  const intl = useIntl();
  const apiRef = useGridApiRef();
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});

  const defaultRow: Row = {
    id: randomUUID(),
    period: 1,
    average: 0,
    rowIndex: 0,
  };

  // #region HANDLERS
  const processRowUpdate = (newRow: GridRowModel<Row>) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleRowInsert = (idx: number) => {
    setRows((oldRows) => [
      ...oldRows,
      {
        ...defaultRow,
        period: rows[rows.length - 1].period + 1,
        rowIndex: idx + 1,
      },
    ]);
    setRowModesModel((oldModel) => ({ ...oldModel }));
  };

  const handleRowDelete = (idx: number) => {
    var newRows = rows.map((r) => r);

    var maxPeriod = newRows.reduce((acc, current) => {
      return Math.max(acc, current.period);
    }, 0);

    // had to apply this validation with pop() since splice was removing the last item but not updating the array in real time
    idx == maxPeriod ? newRows.pop() : newRows.splice(idx, 1);

    for (let i = idx; i < newRows.length; i++) {
      newRows[i].period = newRows[i].period - 1;
    }

    var mappedRows = newRows.map((r) => r);

    setRows(_.sortBy(mappedRows, 'period'));
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  // #endregion HANDLERS

  useEffect(() => {
    let rows: readonly any[] = profile?.length
      ? _.sortBy(profile, 'period').map((line, idx) => {
          return {
            id: randomUUID(),
            period: line.period,
            average: line.average,
            rowIndex: idx,
          };
        })
      : [defaultRow];
    setRows(rows);
  }, [profile]);

  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        headerName: intl.formatMessage({ id: `profilesStrategies.label.period` }),
        field: 'period',
        flex: 1,
        editable: false,
        type: 'number',
        align: 'right',
        headerAlign: 'left',
      },
      {
        headerName: intl.formatMessage({ id: `profilesStrategies.label.average` }),
        field: 'average',
        flex: 1,
        editable: true,
        type: 'number',
        align: 'right',
        headerAlign: 'left',
        renderCell: (props) => {
          return <FormattedNumber value={props.row.average} />;
        },
        preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
          const hasError = !isValidInteger(params.props.value);
          changeErrorDictionary({ ...errorDictionary, [`${params.id}-average`]: hasError });
          return { ...params.props, error: hasError };
        },
      },
      {
        headerName: '',
        field: 'actions',
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        type: 'actions',
        cellClassName: 'actions',
        getActions: (params) => {
          const id = params.row.id;
          const idx = params.row.rowIndex;
          return [
            <GridActionsCellItem
              icon={<MinusCircle />}
              disabled={isDisabled || rows.length === 1}
              label="Remove line"
              onClick={() => handleRowDelete(params.row.period)}
            />,
            <GridActionsCellItem label="Add line" disabled={isDisabled} icon={<PlusCircle />} onClick={() => handleRowInsert(idx)} />,
          ];
        },
      },
    ] as GridColDef[];
  }, [rows, profile]);

  return (
    <Box width={'100%'}>
      <FlexBox>
        <Box>
          <DataGrid
            autoHeight
            hideFooter
            rows={rows}
            apiRef={apiRef}
            disableColumnMenu
            columns={columns}
            disableColumnFilter={false}
            sx={{ background: 'white' }}
            rowModesModel={rowModesModel}
            onRowEditStop={handleRowEditStop}
            processRowUpdate={processRowUpdate}
            onRowModesModelChange={handleRowModesModelChange}
            onCellEditStop={({ field }) => {
              if (field === 'period') return;
              handleEditing();
            }}
            onRowClick={({ id }) => {
              const { field } = apiRef.current?.state.tabIndex.cell ?? {};
              if (field === 'period') return;
              apiRef.current?.getCellParams(id, field as string).isEditable && handleEditing();
            }}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
          />
        </Box>
      </FlexBox>
    </Box>
  );
};

export default React.memo(ProfilesGrid);
